import { API, graphqlOperation } from "aws-amplify";

import { logError } from "../../../utils/appUtils";
import { checkCarrierFieldUnicity } from "../../../utils/carrierIdentity";
import { normalizeCarrierUserType } from "../../../utils/carrierUserUtils";
import { UserType } from "../../../utils/constants";
import { reloadYardCarrierByCarrierByActive } from "../../../graphql/queries";
import {
  inboundShipmentIdsByCarrier,
  outboundShipmentIdsByCarrier,
} from "../../../graphql/carrier/carrier-queries";

export const sendEmails = async (reloadYardAccounts, generateEmailContent) => {
  try {
    if (
      typeof reloadYardAccounts === "object" &&
      !Array.isArray(reloadYardAccounts)
    ) {
      reloadYardAccounts = [reloadYardAccounts];
    }

    const promises = [];
    for (const record of reloadYardAccounts) {
      const emailContent = generateEmailContent(
        record.email,
        `${record.firstName} ${record.lastName}`,
        record.accountName || record.reloadYardName
      );

      const promise = API.post("transREST", "/sendEmail", {
        body: {
          to: record.email,
          bcc: ["support@transloads.co"],
          subject: "Carrier User Deletion Notice",
          template: "emailTemplate.html",
          htmlContent: emailContent,
        },
      });
      promises.push(promise);
    }

    // Wait for all email sending promises to resolve
    await Promise.all(promises);
  } catch (error) {
    logError("managerApp:carriers:carrierUtils", error, false);
  }
};

// Carrier uniqueness now lives in utils/carrierIdentity — validateCarrierUnicity.
// It is shared with the inline create dialog, checks phone and business name as
// well as email, and compares normalised values.

/**
 * The validator that shows an on-blur duplicate on the field that caused it.
 *
 * Appended to each identity field's own validators; the message it returns was
 * written onto the field's data by validateCarrierFieldUnicity below. An empty
 * field says nothing, so clearing one drops a message left from a value that is
 * no longer there.
 */
export const validateCarrierIdentityMeta = (value, allValues, meta) =>
  value ? meta?.data?.error || undefined : undefined;

/**
 * Check one identity field against the yard's carrier book and put the answer
 * where that field's validator will find it.
 *
 * Both create paths — the manager's Carriers form and the inline dialog — hang
 * this off the field's onBlur, so a duplicate is named as soon as the user
 * leaves the field that caused it rather than after the whole form is filled in
 * and saved. The save still runs the full check: this one is the earlier, and
 * more specific, half of the same answer.
 *
 * The form must carry the setFieldData mutator for the message to land.
 *
 * @param {string} source - "businessName", "email" or "phone"
 * @param {object} form - the react-final-form instance
 * @returns {Promise<boolean>} whether the field duplicates another carrier
 */
export const validateCarrierFieldUnicity = async (source, form) => {
  const values = form.getState().values;
  const value = values?.[source];

  if (!value) {
    form.mutators.setFieldData(source, { error: undefined });
    return false;
  }

  const duplicate = await checkCarrierFieldUnicity(source, value, {
    excludeId: values?.id,
    // Carried so the veto applies here too: a carrier whose regulatory numbers
    // contradict this one's is a different company, and must not be reported as
    // a name or address collision the save would then let through.
    identifiers: values?.identifiers,
  });

  // The user can come back and retype the field while the lookup is in flight.
  // The answer is about the value they left, so leave the field to the blur
  // that follows the value it now holds rather than labelling it with this one.
  if (form.getState().values?.[source] !== value) return false;

  form.mutators.setFieldData(source, { error: duplicate?.message });
  return !!duplicate;
};

// The fields the blur checks leave their messages on.
const CARRIER_IDENTITY_SOURCES = ["businessName", "email", "phone"];

/**
 * Drop whatever the blur checks left behind, so the save decides on its own.
 *
 * A blur message is a cached answer, and it can outlive its truth: a duplicate
 * renamed in another tab stays a duplicate here for the cache window, and the
 * message it left keeps the form invalid — which would block the save before
 * its own fresh, fail-closed validateCarrierUnicity ever ran. The advisory
 * check must not gate the authoritative one, so every save path clears the
 * blur messages first and lets the fresh check speak for itself.
 *
 * @param {object} form - the react-final-form instance
 */
export const clearCarrierUnicityMeta = (form) => {
  CARRIER_IDENTITY_SOURCES.forEach((source) =>
    form.mutators.setFieldData(source, { error: undefined }),
  );
};

/**
 * One page of a by-carrier index, read only for whether it holds anything.
 *
 * A token with no items is not "nothing": the @auth filter is applied after
 * DynamoDB has read the page, so a row this session may not see comes back as
 * an empty page that still has more behind it. Counting that as clean is how a
 * legacy shipment with no `groups` gets its carrier deleted out from under it,
 * so the token counts as a hit.
 */
const pageHasRows = (page) =>
  (page?.items?.length ?? 0) > 0 || !!page?.nextToken;

/**
 * Does any shipment name this carrier?
 *
 * Inbounds and outbounds are the only two sources asked: a Timeslot is written
 * with its shipment (processInbound/processOutbound) and a GateCheckIn carries a
 * required shipmentKey, so a carrier with no shipments has neither.
 *
 * @param {string} carrierId
 * @returns {Promise<boolean>}
 */
export const carrierHasShipments = async (carrierId) => {
  const [inbound, outbound] = await Promise.all([
    API.graphql(
      graphqlOperation(inboundShipmentIdsByCarrier, { carrierId, limit: 1 }),
    ),
    API.graphql(
      graphqlOperation(outboundShipmentIdsByCarrier, { carrierId, limit: 1 }),
    ),
  ]);
  return (
    pageHasRows(inbound?.data?.inboundShipmentByCarrierByStatusByDates) ||
    pageHasRows(outbound?.data?.outboundShipmentByCarrierByStatusByDates)
  );
};

/**
 * Every membership row of a carrier, active and retired alike.
 *
 * No condition on `active`, the index's sort key: the retired rows are as much
 * a part of the carrier as the live ones, and a delete that leaves them behind
 * leaves them pointing at nothing.
 *
 * @param {string} carrierId
 * @returns {Promise<object[]>}
 */
export const fetchCarrierJunctions = async (carrierId) => {
  let all = [];
  let nextToken;
  do {
    // eslint-disable-next-line no-await-in-loop
    const response = await API.graphql(
      graphqlOperation(reloadYardCarrierByCarrierByActive, {
        carrierId,
        limit: 250,
        nextToken,
      }),
    );
    const page = response?.data?.reloadYardCarrierByCarrierByActive;
    // Drop nulls the way every other pager here does. @auth can null an
    // individual item on a page, and findCarrierDeleteBlocker reads
    // junction.type off each row — a null would throw and fail the delete gate
    // closed, permanently disabling Delete for a carrier that is really clean.
    const items = page?.items?.filter(Boolean) ?? [];
    if (items.length > 0) all = all.concat(items);
    nextToken = page?.nextToken;
  } while (nextToken);
  return all;
};

// Why a carrier cannot be deleted, in the words the manager is shown.
export const CarrierDeleteBlocker = {
  SHIPMENTS: "Carrier has shipment history — archive it instead.",
  DRIVERS:
    "Carrier has enrolled drivers — delete its drivers first, or archive it.",
  UNKNOWN:
    "Could not confirm this carrier is safe to delete. Please try again.",
};

/**
 * What stands in the way of permanently deleting a carrier, or null if nothing
 * does.
 *
 * Two things do. A shipment's carrierId is a plain pointer with nothing to
 * catch a deleted carrier, so a carrier that ever hauled a load is archive-only
 * forever. And a driver membership is not the yard's to destroy: the row holds
 * the driver's photo, licence attachments and enrolment at this yard, and the
 * manager's own user delete refuses to remove it for exactly that reason — only
 * the driver's carrier can. A carrier whose drivers would go with it is
 * therefore no more deletable than the drivers are.
 *
 * @param {string} carrierId
 * @returns {Promise<string|null>} a CarrierDeleteBlocker message, or null
 */
export const findCarrierDeleteBlocker = async (carrierId) => {
  const [hasShipments, junctions] = await Promise.all([
    carrierHasShipments(carrierId),
    fetchCarrierJunctions(carrierId),
  ]);
  if (hasShipments) return CarrierDeleteBlocker.SHIPMENTS;
  if (
    junctions.some(
      (junction) =>
        normalizeCarrierUserType(junction.type) === UserType.CARRIER_DRIVER,
    )
  ) {
    return CarrierDeleteBlocker.DRIVERS;
  }
  return null;
};
